import { useCallback, useEffect, useMemo, useState } from "react"
import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import {
  buildCharacterReferenceCandidates,
  buildLocationReferenceCandidates,
  buildPropReferenceCandidates,
  entityPickKey,
  referencePickerCandidateUnavailable,
} from "@/lib/canvas-reference-candidates"
import type { CanvasReferenceItem } from "@/types/canvas"
import type { PickerTab } from "./reference-picker-types"

const DEFAULT_TABS: PickerTab[] = ["character", "location", "prop"]

type UseReferencePickerStateArgs = {
  characters: CastRow[]
  locations: LocationRow[]
  props: PropRow[]
  existingKeys: string[]
  onPickReference: (ref: CanvasReferenceItem) => void | Promise<void>
  allowedMediaTypes?: ("image" | "video" | "audio")[]
  /** Subset of tabs; default all three. */
  tabs?: PickerTab[]
  /**
   * Place entities into a scene: ignore attachable/preview gates.
   * Still respects existingKeys when provided.
   */
  entityPick?: boolean
}

export function useReferencePickerState({
  characters,
  locations,
  props,
  existingKeys,
  onPickReference,
  allowedMediaTypes = ["image", "video"],
  tabs: tabsProp,
  entityPick = false,
}: UseReferencePickerStateArgs) {
  const tabs = useMemo(() => (tabsProp && tabsProp.length > 0 ? tabsProp : DEFAULT_TABS), [tabsProp])
  const [pickerTab, setPickerTab] = useState<PickerTab>(() => tabs[0] ?? "character")
  const [previewRef, setPreviewRef] = useState<CanvasReferenceItem | null>(null)

  useEffect(() => {
    if (!tabs.includes(pickerTab)) setPickerTab(tabs[0] ?? "character")
  }, [tabs, pickerTab])

  const characterCandidates = useMemo(() => buildCharacterReferenceCandidates(characters), [characters])
  const locationCandidates = useMemo(() => buildLocationReferenceCandidates(locations), [locations])
  const propCandidates = useMemo(() => buildPropReferenceCandidates(props), [props])

  const pickerCandidates = useMemo(() => {
    const filter = (items: CanvasReferenceItem[]) => items.filter((ref) => allowedMediaTypes.includes(ref.media_type))

    if (pickerTab === "character") return filter(characterCandidates)
    if (pickerTab === "location") return filter(locationCandidates)
    if (pickerTab === "prop") return filter(propCandidates)
    // Primitive tab uses a dedicated shapes grid, not CanvasReferenceItem candidates.
    return []
  }, [pickerTab, characterCandidates, locationCandidates, propCandidates, allowedMediaTypes])

  const pickReference = useCallback(
    (ref: CanvasReferenceItem) => {
      if (entityPick) {
        if (existingKeys.includes(entityPickKey(ref))) return
      } else if (referencePickerCandidateUnavailable(ref, existingKeys)) {
        return
      }
      void Promise.resolve(onPickReference(ref))
    },
    [entityPick, existingKeys, onPickReference],
  )

  return {
    pickerTab,
    setPickerTab,
    previewRef,
    setPreviewRef,
    tabs,
    pickerCandidates,
    pickReference,
  }
}
